// composables/useSessionDateTime.js
import {computed, ref, watch, watchEffect} from "vue";
import {useRoute} from "vue-router";
import useProgram from "@/composables/useProgram";
import history from "@/assets/history.json";
import useTimeZoneSelected from "@/composables/useTimeZoneSelected";

function formatProgramDayDate(dayDate) {
  // expects: "Fri, April 17, 2026"
  const parts = String(dayDate || "")
    .trim()
    .split(/\s+/);
  if (parts.length < 4) return "";

  const month = parts[1];
  const day = parts[2].replace(",", "");
  const year = parts[3];

  const d = new Date(`${day} ${month} ${year}`);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long"
  });
}

export default function useSessionDateTime(session) {
  const route = useRoute();
  const year = computed(() => String(route.params?.year || ""));
  // the zone the page's program table shows (its menu; Program/TimeZone.vue), until it has chosen
  // one the program's own zone: the date and time follow the zone the user is reading
  const tableTimeZone = useTimeZoneSelected();
  const timeZoneSelected = computed(() => {
    return tableTimeZone.value || history.years?.[year.value] || "";
  });

  const program = ref({});

  const load = async () => {
    program.value = await useProgram({
      timeZoneSelected: timeZoneSelected.value, // REQUIRED by useProgram()
      session,
      year: route.params?.year
    });
  };

  watch(timeZoneSelected, () => void load(), {immediate: true});

  const firstDayKey = computed(() => Object.keys(program.value || {})[0] || "");

  const date = computed(() => {
    const key = firstDayKey.value;
    if (!key) return "";
    const dayDate = program.value?.[key]?.date;
    return dayDate ? formatProgramDayDate(dayDate) : "";
  });

  const time = computed(() => {
    const key = firstDayKey.value;
    if (!key) return "";

    const events = program.value?.[key]?.events || [];
    const timed = events.filter((e) => e?.start);
    if (!timed.length) return "";

    const start = timed[0].start;
    const last = timed[timed.length - 1];
    const end = last.end || last.start;

    const startHour = start.toFormat("h");
    const endHour = end.toFormat("h");
    const startSuffix = start.toFormat("a").toLowerCase(); // am / pm
    const endSuffix = end.toFormat("a").toLowerCase();

    if (startSuffix === endSuffix) {
      // same half of day → collapse suffix
      return `${startHour}–${endHour}${endSuffix}`;
    }

    // different → must show both
    return `${startHour}${startSuffix}–${endHour}${endSuffix}`;
  });

  watchEffect(() => {
    console.log("sessionDateTime program:", JSON.stringify(program.value));
  });

  return {date, time};
}
